import type { Observable } from 'rxjs';
import { of, pipe, throwError } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import type {
  Blueprint,
  ReportDashboardComponentViewData,
  VisualizationDataTypeUnion,
  VisualizationInputMeta,
  VisualizationMapperOperatorInput,
} from '@core/reports-types';

export const matchBlueprintFnFactory =
  ({ match }: Pick<Blueprint<any>, 'match'>) =>
  ({
    component,
    meta,
  }: {
    component: ReportDashboardComponentViewData;
    meta: VisualizationInputMeta;
  }): boolean => {
    if (typeof match === 'function') {
      return match(component, meta);
    }
    const keys = Object.keys(match);
    return keys.every(
      (key: keyof ReportDashboardComponentViewData) => component[key] === match[key],
    );
  };
// Blueprint can provide match as either a function, which taks the component and returns true if it matches,
// or
// as a partial of ReportDashboardComponent, which is matched against the component
export const fromBlueprints = <T extends VisualizationMapperOperatorInput>(
  collection: Array<Blueprint<any>>,
) =>
  pipe(
    switchMap<T, Observable<VisualizationDataTypeUnion>>(
      ({ component, dataset, metric, meta }): Observable<VisualizationDataTypeUnion | never> => {
        const blueprint = collection.find(({ match, factory }) =>
          matchBlueprintFnFactory({ match })({ component, meta }),
        );

        return blueprint
          ? of(blueprint.factory({ component, dataset, metric, meta }))
          : throwError(
              `No blueprint found for component.id: ${(<any>component).id} (kind: ${
                component.kind
              }, viewMode: ${component.viewMode}, chartType: ${component.chartType})`,
            );
      },
    ),
  );
